/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
// import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetLeadsGroupBy } from '../../helpers/ChartHelper';
// import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Doughnut, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartEvent, ActiveElement, Chart } from 'chart.js';
import { useTranslation } from 'react-i18next';
import { TranslationTextList } from '../../locales/common';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvChartAnimation, rvTooltipStyle, rvLegendLabels, isChartDataEmpty } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
ChartJS.register(ArcElement, Tooltip, Legend);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

interface EnquiriesByGroupChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    groupBy: 'LikelihoodToProceed' | 'Quality';
}

interface ChartState {
    symbol: string;
    counts: number[];
    estimatedFees: number[];
    ratings: string[];
    openEnquiriesCount: number;
    openEnquiryCountWithoutFeeEstimate: number;
}

export const EnquiriesByGroupChart: React.FC<EnquiriesByGroupChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line: no-any
    const chartRef = React.useRef<any>();

    const { t } = useTranslation() as {
        t: (key: keyof TranslationTextList) => string;
    };

    const enquiriesByGroupQuery = useQuery<EnquiriesByGroupData>(ENQUIRIES_BY_GROUP_QUERY, {
        variables: {
            groupBy: props.groupBy,
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            // responsibility: props.chartFilter?.responsibility,
        },
        fetchPolicy: 'cache-and-network',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    const [state, setState] = useState<ChartState>({
        symbol: '$',
        counts: [],
        estimatedFees: [],
        ratings: [],
        openEnquiriesCount: 0,
        openEnquiryCountWithoutFeeEstimate: 0,
    });

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (
            enquiriesByGroupQuery.data &&
            enquiriesByGroupQuery.data.statistics &&
            enquiriesByGroupQuery.data.statistics.lead &&
            enquiriesByGroupQuery.data.statistics.lead.enquiriesByGroup &&
            !enquiriesByGroupQuery.loading &&
            !enquiriesByGroupQuery.error
        ) {
            const { groupedEnquiries, openEnquiriesCount, openEnquiryCountWithoutFeeEstimate } = enquiriesByGroupQuery.data.statistics.lead.enquiriesByGroup;

            const counts: number[] = groupedEnquiries.map((groupedEnquiry: GroupedEnquiry) => groupedEnquiry.count);
            const estimatedFees: number[] = groupedEnquiries.map((groupedEnquiry: GroupedEnquiry) => groupedEnquiry.estimatedFees);
            const ratings: string[] = groupedEnquiries.map((groupedEnquiry: GroupedEnquiry) => {
                let labelExtension = 'No Rating';

                if (groupedEnquiry.rating === 1) {
                    labelExtension = `${groupedEnquiry.rating} Star`;
                }
                if (groupedEnquiry.rating > 1) {
                    labelExtension = `${groupedEnquiry.rating} Stars`;
                }

                return labelExtension;
            });

            setState((prevState) => {
                return {
                    ...prevState,
                    counts,
                    estimatedFees,
                    ratings,
                    openEnquiriesCount,
                    openEnquiryCountWithoutFeeEstimate,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [enquiriesByGroupQuery.loading, enquiriesByGroupQuery.error, enquiriesByGroupQuery.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isEnquiriesBy${props.groupBy}ChartOpen`);
        }
    };

    const handleTotalClick = () => {
        // if (props.getSelectedItem) {
        //     const searchCriterias = new Array<SearchCriteria>();
        //     searchCriterias.push(
        //         {property: 'MatterInvoiceDates', operator: 'gt', comparison: '0'}
        //     );
        //     props.getSelectedItem(
        //         searchCriterias,
        //         true,
        //         `WIP Lockup Days by ${GetGroupBy(props.groupBy)}`,
        //         DrillDownType.matterSummaryDrilldown,
        //         'Unbilled Fees'
        //     );
        // }
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    if (enquiriesByGroupQuery.loading) {
        return (
            <ChartSkeleton variant="pie" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<PeopleOutlinedIcon />} title={<>{t('Enquiries')} by {GetLeadsGroupBy(props.groupBy)}</>} />
                <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                    <AnimatedNumber value={state.openEnquiriesCount} format={(amount) => `${formatNumber(amount)}`} />
                </div>
                <div className={classes.title}>Open {t('Enquiries')}</div>
            </div>
            <div className={classes.barContainer}>
                <Doughnut
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            // const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.ratings[_index];

                            // const searchCriterias = new Array<SearchCriteria>();

                            // // . e.g. - Apr 22, Apr 23, etc
                            // props.getSelectedItem(
                            //     searchCriterias, 
                            //     false, 
                            //     'Time Recorded - ' + item, 
                            //     DrillDownType.timesheetSummary, 
                            //     'Timesheet Lines'
                            // );
                        }
                    }}
                    data={{
                        labels: state.ratings,
                        datasets: [
                            {
                                data: state.counts,
                                backgroundColor: [RV_CHART_COLORS.faint, RV_CHART_COLORS.red, RV_CHART_COLORS.amber, '#facc15', RV_CHART_COLORS.green, RV_CHART_COLORS.primary],
                                hoverBackgroundColor: [RV_CHART_COLORS.faint, RV_CHART_COLORS.red, RV_CHART_COLORS.amber, '#facc15', RV_CHART_COLORS.green, RV_CHART_COLORS.primary],
                                borderColor: '#ffffff',
                                borderWidth: 2,
                                hoverOffset: 8,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        animation: { ...rvChartAnimation(), animateRotate: true, animateScale: true },
                        layout: { padding: 8 },
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: '',
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            // dataIndex,
                                            // dataset,
                                            label,
                                            formattedValue,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${label}: ${formatNumber(currentValue)}`;
                                    },
                                    // tslint:disable-next-line: no-any
                                    afterLabel: function (context: any) {
                                        const {
                                            dataIndex,
                                            // label,
                                            // formattedValue,
                                        } = context;
                                        const estimatedFees = state.estimatedFees[dataIndex];

                                        return `Estimated Fees: ${state.symbol}${formatNumber(estimatedFees)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.counts) && (
                    <ChartEmptyState title="No enquiries" hint="Nothing to show for this selection." />
                )}
            </div>
            <span className={classes.span}>{formatNumber(state.openEnquiryCountWithoutFeeEstimate)} open {t('enquiries')} have no Fee Estimate</span>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface EnquiriesByGroupData {
    statistics: Statistics;
}

export interface Statistics {
    lead: Lead;
}

export interface Lead {
    enquiriesByGroup: EnquiriesByGroup;
}

export interface EnquiriesByGroup {
    openEnquiriesCount: number;
    openEnquiryCountWithoutFeeEstimate: number;
    groupedEnquiries: GroupedEnquiry[];
}

export interface GroupedEnquiry {
    count: number;
    estimatedFees: number;
    rating: number;
}

const ENQUIRIES_BY_GROUP_QUERY = gql`
    query enquiriesByGroup($groupBy: NewLeadsGroupBy, $entityType: Int, $entityGuid: String) {
        statistics {
            lead {
                enquiriesByGroup(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid) {
                    openEnquiriesCount
                    openEnquiryCountWithoutFeeEstimate
                    groupedEnquiries {
                        count
                        estimatedFees
                        rating
                    }
                }
            }
        }
    }
`;
